import { useEffect } from 'react';
// import { useQueueStore } from '@/lib/store';
import { usePatientsStore } from '@/lib/stores/patients';
import { useHospitalProfileStore } from '@/lib/stores/hospitalProfile';
import { Patient } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, Stethoscope, CheckCircle, XCircle, MoreHorizontal, MessageCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { safeFormatDateOnly } from '@/lib/utils/dateUtils';
import { MainLayout } from '@/components/layout/MainLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { openWhatsAppChat } from '@/lib/utils/whatsapp';

const FollowUps = () => {
  const patients = usePatientsStore((s) => s.patients);
  const fetchPatients = usePatientsStore((s) => s.fetchPatients);
  const updatePatient = usePatientsStore((s) => s.updatePatient);
  const hospitalName = useHospitalProfileStore((s) => s.profile.name);
  const location = useLocation();
  
  // Check if this is a standalone page (staff role) or tab content (admin role)
  const isStandalonePage = location.pathname === '/followups';

  useEffect(() => {
    // Fetch patients when component mounts
    fetchPatients();
  }, [fetchPatients]);

  const isResolvedFollowUp = (followUpStatus?: string) =>
    followUpStatus === 'visited' || followUpStatus === 'absent';

  // Filter patients with follow-up dates in the future
  const patientsWithFollowUps = patients.filter(patient => {
    if (!patient.followUpDate) return false;
    if (isResolvedFollowUp(patient.followUpStatus)) return false;
    const followUpDate = new Date(patient.followUpDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return followUpDate >= today;
  });

  // Include overdue too for "All" view
  const overdueFollowUps = patients.filter(p => {
    if (!p.followUpDate) return false;
    if (isResolvedFollowUp(p.followUpStatus)) return false;
    const d = new Date(p.followUpDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  });

  // Sort by follow-up date (earliest first)
  const sortedFollowUps = patientsWithFollowUps.sort((a, b) => {
    const dateA = new Date(a.followUpDate!);
    const dateB = new Date(b.followUpDate!);
    return dateA.getTime() - dateB.getTime();
  });

  // Calculate statistics
  const stats = {
    total: patientsWithFollowUps.length,
    today: patientsWithFollowUps.filter(p => {
      const followUpDate = new Date(p.followUpDate!);
      const today = new Date();
      return followUpDate.toDateString() === today.toDateString();
    }).length,
    thisWeek: patientsWithFollowUps.filter(p => {
      const followUpDate = new Date(p.followUpDate!);
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      return followUpDate >= today && followUpDate <= nextWeek;
    }).length,
    thisMonth: patientsWithFollowUps.filter(p => {
      const followUpDate = new Date(p.followUpDate!);
      const today = new Date();
      const nextMonth = new Date(today);
      nextMonth.setMonth(today.getMonth() + 1);
      return followUpDate >= today && followUpDate <= nextMonth;
    }).length,
    overdue: patients.filter(p => {
      if (!p.followUpDate) return false;
      const followUpDate = new Date(p.followUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return followUpDate < today;
    }).length
  };

  const getFollowUpStatus = (followUpDate?: string, followUpStatus?: string) => {
    if (followUpStatus === 'visited') {
      return { status: 'visited', label: 'Visited', color: 'bg-green-100 text-green-800' };
    }
    if (followUpStatus === 'absent') {
      return { status: 'absent', label: 'Absent', color: 'bg-orange-100 text-orange-800' };
    }
    if (!followUpDate) {
      return { status: 'scheduled', label: 'Scheduled', color: 'bg-green-100 text-green-800' };
    }
    const date = new Date(followUpDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUpDay = new Date(date);
    followUpDay.setHours(0, 0, 0, 0);

    if (followUpDay.getTime() === today.getTime()) {
      return { status: 'today', label: 'Today', color: 'bg-blue-100 text-blue-800' };
    } else if (followUpDay < today) {
      return { status: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-800' };
    } else {
      const diffDays = Math.ceil((followUpDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays <= 7) {
        return { status: 'upcoming', label: `In ${diffDays} day${diffDays > 1 ? 's' : ''}`, color: 'bg-yellow-100 text-yellow-800' };
      } else {
        return { status: 'scheduled', label: 'Scheduled', color: 'bg-green-100 text-green-800' };
      }
    }
  };

  // Build datasets for filters
  const pendingFollowUps = [...overdueFollowUps, ...sortedFollowUps];
  const visitedFollowUps = patients.filter(p => p.followUpStatus === 'visited');
  const absentFollowUps = patients.filter(p => p.followUpStatus === 'absent');
  // De-duplicate by id to avoid duplicate React keys when combining lists
  const dedupeById = (arr: Patient[]) => {
    const map = new Map<string, Patient>();
    for (const p of arr) map.set(p.id, p);
    return Array.from(map.values());
  };
  const allFollowUps = dedupeById([...pendingFollowUps, ...visitedFollowUps, ...absentFollowUps]);

  const handleMarkVisited = (patientId: string) => {
    void updatePatient(patientId, { followUpStatus: 'visited', followUpDate: null });
  };

  const handleMarkAbsent = (patientId: string) => {
    void updatePatient(patientId, { followUpStatus: 'absent' });
  };

  const handleReschedule = (patient: Patient) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysInput = window.prompt(
      'Reschedule after how many days?',
      '7'
    );

    if (!daysInput) return;

    const daysToAdd = Number.parseInt(daysInput, 10);
    if (!Number.isInteger(daysToAdd) || daysToAdd <= 0) {
      window.alert('Please enter a valid positive number of days.');
      return;
    }

    const nextFollowUpDate = new Date(today);
    nextFollowUpDate.setDate(nextFollowUpDate.getDate() + daysToAdd);

    void updatePatient(patient.id, {
      followUpStatus: 'scheduled',
      followUpDate: nextFollowUpDate.toISOString(),
    });
  };

  const handleWhatsAppReminder = (patient: Patient) => {
    const clinicName = hospitalName?.trim() || 'our clinic';
    const followUpDateText = patient.followUpDate
      ? safeFormatDateOnly(patient.followUpDate)
      : 'the scheduled date';
    const message = `Hello ${patient.name},

This is a reminder from ${clinicName} for your follow-up visit on ${followUpDateText}.

Please reply here or call us if you need to reschedule.

Thank you.`;

    const opened = openWhatsAppChat(patient.contactNumber, message);
    if (!opened) {
      window.alert('Invalid or missing contact number for WhatsApp.');
    }
  };

  const content = (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Calendar className="h-6 w-6 text-hospital-primary" />
        <h1 className="text-2xl font-bold">Follow-ups</h1>
      </div>
        
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">Total Follow-ups</h3>
            <p className="text-2xl font-semibold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">Today</h3>
            <p className="text-2xl font-semibold text-blue-600">{stats.today}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">This Week</h3>
            <p className="text-2xl font-semibold text-yellow-600">{stats.thisWeek}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">This Month</h3>
            <p className="text-2xl font-semibold text-green-600">{stats.thisMonth}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">Overdue</h3>
            <p className="text-2xl font-semibold text-red-600">{stats.overdue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Follow-ups list */}
      <div className="space-y-4">
        
        <Tabs defaultValue="pending" className="space-y-3">
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full">
            <TabsTrigger 
              value="pending" 
              className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
            >
              <span className="hidden sm:inline">Pending ({pendingFollowUps.length})</span>
              <span className="sm:hidden">Pending ({pendingFollowUps.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="visited" 
              className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
            >
              <span className="hidden sm:inline">Visited ({visitedFollowUps.length})</span>
              <span className="sm:hidden">Visited ({visitedFollowUps.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="absent" 
              className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
            >
              <span className="hidden sm:inline">Absent ({absentFollowUps.length})</span>
              <span className="sm:hidden">Absent ({absentFollowUps.length})</span>
            </TabsTrigger>
            <TabsTrigger 
              value="all" 
              className="text-xs sm:text-sm transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:font-semibold data-[state=active]:scale-105 data-[state=active]:ring-2 data-[state=active]:ring-blue-400/50 data-[state=inactive]:hover:bg-gray-200/50"
            >
              <span className="hidden sm:inline">All ({allFollowUps.length})</span>
              <span className="sm:hidden">All ({allFollowUps.length})</span>
            </TabsTrigger>
          </TabsList>
          {(allFollowUps.length) === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">No follow-ups scheduled</h3>
              <p className="text-sm">
                Follow-ups will appear here when doctors schedule them in prescriptions.
              </p>
            </div>
          ) : (
              <>
              {['pending','visited','absent','all'].map((tab) => (
                <TabsContent key={tab} value={tab}>
                  {/* Mobile Layout */}
                  <div className="block sm:hidden space-y-3">
                    {(tab === 'pending' ? pendingFollowUps : tab === 'visited' ? visitedFollowUps : tab === 'absent' ? absentFollowUps : allFollowUps).map((patient) => {
                      const statusInfo = getFollowUpStatus(patient.followUpDate || undefined, patient.followUpStatus);
                      return (
                        <Card key={patient.id} className="p-4">
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <Link to={`/patient/${patient.id}`} className="hover:text-hospital-primary font-medium text-base" title={patient.name}>
                                  {patient.name}
                                </Link>
                                <div className="text-sm text-gray-600 mt-1">
                                  {patient.age} / {patient.gender} • {patient.contactNumber || 'No contact'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={statusInfo.color}>{statusInfo.label}</Badge>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {!isResolvedFollowUp(patient.followUpStatus) && (
                                      <DropdownMenuItem onClick={() => handleWhatsAppReminder(patient)}>
                                        <MessageCircle className="h-4 w-4 text-green-600 mr-2" /> WhatsApp Reminder
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => handleMarkVisited(patient.id)}>
                                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> Mark Visited
                                    </DropdownMenuItem>
                                    {patient.followUpStatus === 'absent' ? (
                                      <DropdownMenuItem onClick={() => handleReschedule(patient)}>
                                        <Calendar className="h-4 w-4 text-blue-600 mr-2" /> Reschedule
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem onClick={() => handleMarkAbsent(patient.id)}>
                                        <XCircle className="h-4 w-4 text-orange-600 mr-2" /> Mark Absent
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              <div><strong>Follow-up:</strong> {patient.followUpDate ? safeFormatDateOnly(patient.followUpDate) : '-'}</div>
                              <div><strong>Registered:</strong> {safeFormatDateOnly(patient.registeredAt)}</div>
                              <div><strong>Reason:</strong> {patient.reason}</div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:block rounded-md border overflow-auto max-h-[60vh]">
                    <Table className="table-fixed w-full">
                      <TableHeader className="sticky top-0 bg-background">
                        <TableRow>
                          <TableHead className="w-40">Patient</TableHead>
                          <TableHead className="w-20">Status</TableHead>
                          <TableHead className="w-24">Contact</TableHead>
                          <TableHead className="w-28">Age/Gender</TableHead>
                          <TableHead className="w-28">Follow-up</TableHead>
                          <TableHead className="w-28">Registered</TableHead>
                          <TableHead className="w-32">Reason</TableHead>
                          <TableHead className="w-20 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(tab === 'pending' ? pendingFollowUps : tab === 'visited' ? visitedFollowUps : tab === 'absent' ? absentFollowUps : allFollowUps).map((patient) => {
                          const statusInfo = getFollowUpStatus(patient.followUpDate || undefined, patient.followUpStatus);
                          return (
                            <TableRow key={patient.id}>
                              <TableCell className="w-40 font-medium">
                                <Link to={`/patient/${patient.id}`} className="hover:text-hospital-primary truncate block" title={patient.name}>
                                  {patient.name}
                                </Link>
                              </TableCell>
                              <TableCell className="w-20">
                                <Badge variant="outline" className={statusInfo.color}>{statusInfo.label}</Badge>
                              </TableCell>
                              <TableCell className="w-24 text-blue-600 font-semibold">{patient.contactNumber || '-'}</TableCell>
                              <TableCell className="w-28">{patient.age} / {patient.gender}</TableCell>
                              <TableCell className="w-28">{patient.followUpDate ? safeFormatDateOnly(patient.followUpDate) : '-'}</TableCell>
                              <TableCell className="w-28">{safeFormatDateOnly(patient.registeredAt)}</TableCell>
                              <TableCell className="w-32 truncate" title={patient.reason}>{patient.reason}</TableCell>
                              <TableCell className="w-20 text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {!isResolvedFollowUp(patient.followUpStatus) && (
                                      <DropdownMenuItem onClick={() => handleWhatsAppReminder(patient)}>
                                        <MessageCircle className="h-4 w-4 text-green-600 mr-2" /> WhatsApp Reminder
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => handleMarkVisited(patient.id)}>
                                      <CheckCircle className="h-4 w-4 text-green-600 mr-2" /> Mark Visited
                                    </DropdownMenuItem>
                                    {patient.followUpStatus === 'absent' ? (
                                      <DropdownMenuItem onClick={() => handleReschedule(patient)}>
                                        <Calendar className="h-4 w-4 text-blue-600 mr-2" /> Reschedule
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem onClick={() => handleMarkAbsent(patient.id)}>
                                        <XCircle className="h-4 w-4 text-orange-600 mr-2" /> Mark Absent
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              ))}
              </>
            )}
        </Tabs>
      </div>
    </div>
  );

  // Return with MainLayout for standalone page, without for tab content
  return isStandalonePage ? (
    <MainLayout>
      {content}
    </MainLayout>
  ) : (
    content
  );
};

export default FollowUps;
